/* eslint-disable max-lines */
import {
  BarChartOutlined,
  ClearOutlined,
  ConsoleSqlOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeInvisibleOutlined,
  FilterOutlined,
  FunctionOutlined,
  HighlightOutlined,
  ImportOutlined,
  NodeIndexOutlined,
  PlusOutlined,
  PushpinOutlined,
  RightOutlined,
  SearchOutlined,
  SettingOutlined,
  SortAscendingOutlined,
  SortDescendingOutlined,
} from '@ant-design/icons'
import type { ViewFieldsFragment } from '@salescore/client-api'
import { CONSTANT, POSTHOG_EVENTS, posthogTrackView } from '@salescore/client-base'
import {
  canEditViewConfigField,
  canViewSearchSqlModal,
  SuspenseWithLoading,
  useCanForView,
  useMe,
} from '@salescore/client-common'
import {
  type CompiledFormViewConfig,
  type CompiledKpiPivotViewConfig,
  type CompiledKpiViewConfig,
  type CompiledSheetViewConfig,
  fieldMetaTypes,
  isSameNodeProperty,
  type ViewConfigField,
  viewConfigFieldSchema,
  type ViewConfigSheet,
  type ViewConfigTreeNode,
  type ViewQueryField,
  type ViewQueryList,
  type ViewQueryRecordNode,
} from '@salescore/core'
import { canUseOrganizationLicense, ORGANIZATION_LICENSES } from '@salescore/features'
import { disableOnClickOutsideProperties, PopconfirmIf, useOnClickOutside } from '@salescore/frontend-common'
import { App, Button, Tag, Tooltip } from 'antd'
import { isNotNullish } from 'effect/Predicate'
import { t } from 'i18next'
import { type CSSProperties, type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useRecoilState, useRecoilValue } from 'recoil'

import { DimensionAdditionForm } from '../../../components/view_ui/ViewUIKpi/DimensionAdditionForm'
import { DimensionFilterPicker } from '../../../components/view_ui/ViewUIKpi/DimensionFilterPicker'
import { DimensionSorter } from '../../../components/view_ui/ViewUIKpi/DimensionSorter'
import { KpiAdditionForm } from '../../../components/view_ui/ViewUIKpiPivot/KpiAdditionForm'
import { PropertySelectorTableForContextMenu } from '../../../components/view_ui/ViewUISheet/PropertySelectorTableForContextMenu'
import { SheetCustomModelPropertyEditForm } from '../../../components/view_ui/ViewUISheet/PropertySelectorTableForContextMenu/SheetCustomModelPropertyEditForm'
import { buildDynamicSelectOptionModalProperties } from '../../../components/view_ui/ViewUISheetWithNavigation/SheetNavigation/DynamicSelectOptionModal'
import type { ViewConfigFilterNodeForForm } from '../../../components/view_ui/ViewUISheetWithNavigation/SheetNavigation/filter/common'
import { buildViewJoinFiltersSettingModalProperties } from '../../../components/view_ui/ViewUISheetWithNavigation/SheetNavigation/filter/ViewJoinFiltersSettingModal'
import { buildImportFieldsModalProperties } from '../../../components/view_ui/ViewUISheetWithNavigation/SheetNavigation/ImportFieldsModal'
import { buildNodeSearchSqlFormModalProperties } from '../../../components/view_ui/ViewUISheetWithNavigation/SheetNavigation/NodeSearchSqlFormModal'
import { buildSearchSqlFormModalProperties } from '../../../components/view_ui/ViewUISheetWithNavigation/SheetNavigation/SearchSqlFormModal'
import { buildViewQueryFiltersModalProperties } from '../../../components/view_ui/ViewUISheetWithNavigation/SheetNavigation/ViewQueryFiltersModal'
import { buildViewQueryNodeDetailModalProperties } from '../../../components/view_ui/ViewUISheetWithNavigation/SheetNavigation/ViewQueryNodeDetailModal'
import { buildViewDebugModalProperties } from '../../../components/view_ui/ViewUISheetWithNavigation/ViewDebugModal'
import type {
  PivotColumn,
  PivotRowColumn,
  PivotSystemColumn,
  RSheetColumn,
  RSheetRecordNode,
  SheetColumn,
} from '../../../domain/column'
import { extractKpiId } from '../../../kpi/utility'
import type { SheetTableContext } from '../../../recoil/context'
import { kpiParameterAtom } from '../../../recoil/navigation/atoms'
import { convertToFilterNodeForForm } from '../../../recoil/navigation/hooks'
import { useRecordsImpl } from '../../../recoil/records'
import { changesAtom } from '../../../recoil/recordsHistory'
import { sheetCustomModelsAtom, viewsContextAtom } from '../../../recoil/view/atoms'
import { getCanEditField } from '../../../recoil/view/functions/filterRelated'
import { queryFieldToConfigField } from '../../../recoil/view/mutations/field/util'
import { useGetModelProperty } from '../../../recoil/view/selectors/connectionsSelector'
import {
  useEditNodeMutation,
  useRemoveField,
  useRemoveFilterByField,
  useRemoveSorterMutation,
  useSetFieldMutation,
  useSetUiColumn,
  useUpsertSorter,
  useViewConfigMutation,
  useViewConfigSheet,
} from '../../../recoil/viewConfig'
import { flatNodes } from '../../../state/nodeUtility'
import { RSheetsStyle } from '../../util/RSheetsStyle'
import {
  ContextMenu,
  type ContextMenuItem,
  useHeaderColumnContextMenu,
  useHeaderRowContextMenu,
  useKpiContextMenu,
} from '../common/ContextMenu'
import { buildConditionalEffectsFormModalProperties } from '../modal/ConditionalEffectsFormModal'
import { buildHighlightFormModalProperties } from '../modal/HighlightFormModal'
import { buildMeasureFormModalProperties } from '../modal/MeasureFormModal'
import { resolveHeaderSorterSortBy } from './resolveHeaderSorterSortBy'

const width = 180
const menuStyle: CSSProperties = {
  // paddingRight: 8,
  position: 'absolute',
  width,
  left: 0, // right: 0もありだが、固定カラムの右隣で実行すると被る問題がz-indexの関係的に解決できなかった
  top: 40,
  backgroundColor: 'white',
  boxShadow: '0 2px 6px 2px rgb(60 64 67 / 15%)',
  borderRadius: 2,
  zIndex: RSheetsStyle.zIndex.contextMenu,
}

export const KpiHeaderContextMenu = ({
  column,
  hide,
  columns,
  compiledConfig,
  view,
  setHasChangeToViewSchema,
}: {
  column: PivotRowColumn<ViewQueryRecordNode> | PivotColumn<ViewQueryRecordNode>
  hide: () => void
  columns: Array<
    PivotSystemColumn<ViewQueryRecordNode> | PivotRowColumn<ViewQueryRecordNode> | PivotColumn<ViewQueryRecordNode>
  >
  compiledConfig: CompiledKpiViewConfig | CompiledKpiPivotViewConfig
  view: ViewFieldsFragment
  setHasChangeToViewSchema: (hasChangeToViewSchema: boolean) => void
}): ReactNode => {
  const reference = useRef<HTMLDivElement>(null)
  // 子の ContextMenu が出す Popconfirm の popup は data 属性を付けられないため antd のクラスで除外する
  useOnClickOutside(reference, () => {
    hide()
  }, ['anticon', 'ant-popconfirm'])
  useEffect(() => {
    if (reference.current !== null) {
      reference.current.focus()
    }
  }, [])

  const [kpiPivotParameter] = useRecoilState(kpiParameterAtom)
  const isRowColumn = column.index <= kpiPivotParameter.pivot.rows.length // こんなんでいいだろうか？
  const rowIndex = isRowColumn ? column.index - 1 : -1
  const [dimensionFilterDropdownVisible, setDimensionFilterDropdownVisible] = useState(false)
  const [dimensionSorterDropdownVisible, setDimensionSorterDropdownVisible] = useState(false)
  const [addKpiDropdownVisible, setAddKpiDropdownVisible] = useState(false)
  const [addRowDimensionDropdownVisibile, setAddRowDimensionDropdownVisible] = useState(false)
  const kpiId = useMemo(() => extractKpiId(kpiPivotParameter, column), [column, kpiPivotParameter])
  const kpiContextMenu = useKpiContextMenu({
    kpiId,
    setAddKpiDropdownVisible,
    hideContextMenu: hide,
    addKpiDirection: 'right',
    view,
    setHasChangeToViewSchema,
  })
  const headerColumnContextMenu = useHeaderColumnContextMenu({
    column,
    hideContextMenu: hide,
    setHasChangeToViewSchema,
  })
  const headerRowContextMenu = useHeaderRowContextMenu({
    setDimensionFilterDropdownVisible,
    setDimensionSorterDropdownVisible,
    setAddRowDimensionDropdownVisible,
    hideContextMenu: hide,
    rowIndex,
    column,
    columns,
    view,
    setHasChangeToViewSchema,
  })

  const { field } = column

  return (
    <div>
      <ContextMenu
        menu={isRowColumn ? headerRowContextMenu : [...headerColumnContextMenu, ...kpiContextMenu]}
        hideContextMenu={hide}
        style={{
          position: 'absolute',
          width,
          left: 0, // right: 0もありだが、固定カラムの右隣で実行すると被る問題がz-indexの関係的に解決できなかった
          top: 40,
        }}
      />
      {dimensionFilterDropdownVisible && (
        <div
          {...disableOnClickOutsideProperties}
          className="absolute top-0 bg-white p-2"
          style={{
            ...menuStyle,
            width: 400, // ここを変更する際は、transition.lessでslide-leftも変更する必要がある
            top: 0,
            left: width + 1,
          }}
        >
          <SuspenseWithLoading>
            <DimensionFilterPicker hide={hide} field={field} view={view} />
          </SuspenseWithLoading>
        </div>
      )}
      {dimensionSorterDropdownVisible && (
        <div
          {...disableOnClickOutsideProperties}
          className="absolute top-0 bg-white p-2"
          style={{
            ...menuStyle,
            width: 400, // ここを変更する際は、transition.lessでslide-leftも変更する必要がある
            top: 0,
            left: width + 1,
          }}
        >
          <DimensionSorter hide={hide} field={field} />
        </div>
      )}
      {addRowDimensionDropdownVisibile && (
        <div
          {...disableOnClickOutsideProperties}
          className="absolute top-0 bg-white p-2"
          style={{
            ...menuStyle,
            width: 400, // ここを変更する際は、transition.lessでslide-leftも変更する必要がある
            top: 0,
            left: width + 1,
          }}
        >
          <DimensionAdditionForm hide={hide} prevRowIndex={rowIndex} compiledConfig={compiledConfig} />
        </div>
      )}
      {addKpiDropdownVisible && (
        <div
          {...disableOnClickOutsideProperties}
          className="absolute top-0 bg-white p-2"
          style={{
            ...menuStyle,
            width: 320,
            top: 0,
            left: width + 1,
          }}
        >
          <KpiAdditionForm hide={hide} prevKpiId={kpiId} view={view} />
        </div>
      )}
    </div>
  )
}

export const SheetHeaderContextMenu = ({
  column,
  hide,
  context,
  columns,
  compiledConfig,
  view,
  query,
  setHasChangeToViewSchema,
}: {
  column: SheetColumn<ViewQueryRecordNode>
  context: SheetTableContext
  hide: () => void
  columns: Array<RSheetColumn<RSheetRecordNode>>
  compiledConfig: CompiledSheetViewConfig | CompiledKpiViewConfig | CompiledKpiPivotViewConfig | CompiledFormViewConfig
  view: ViewFieldsFragment
  query: ViewQueryList
  setHasChangeToViewSchema: (hasChangeToViewSchema: boolean) => void
  // eslint-disable-next-line complexity,sonarjs/cognitive-complexity
}): ReactNode => {
  const { modal, message } = App.useApp()
  const viewsContext = useRecoilValue(viewsContextAtom)
  const reference = useRef<HTMLDivElement>(null)
  useOnClickOutside(reference, () => {
    hide()
  }, [
    'ant-dropdown-menu-title-content',
    'ant-dropdown',
    'ant-select-dropdown',
    'ant-select-item-option-content',
    'ant-popover',
    'anticon',
  ])

  useEffect(() => {
    if (reference.current !== null) {
      reference.current.focus()
    }
  }, [])

  const { node, field } = column
  const config = useViewConfigSheet()
  const flattenNodes = useMemo(() => flatNodes(config.tree), [config.tree])
  const getNode = useCallback((nodeName: string) => flattenNodes.find((x) => x.name === nodeName), [flattenNodes])
  const me = useMe()
  const canDisplayConfigEdit = useCanForView('display-config-edit', view)
  const [firstRecord] = useRecordsImpl()
  const canEditField = getCanEditField(me) && canDisplayConfigEdit
  const upsertSorter = useUpsertSorter()
  const getModelProperty = useGetModelProperty()
  const sorterConfigField = queryFieldToConfigField(field, config.fields ?? [])
  const sorterSortBy = resolveHeaderSorterSortBy({
    sorters: config.sorters,
    property: sorterConfigField?.property,
    modelProperty:
      sorterConfigField === undefined
        ? undefined
        : getModelProperty(sorterConfigField.property.modelName, sorterConfigField.property.propertyName),
  })
  const cancel = useRemoveSorterMutation()
  const remove = useRemoveField()
  const setUiColumn = useSetUiColumn()
  const setField = useSetFieldMutation()
  const { setMeta } = useViewConfigMutation()

  const removeFilterByField = useRemoveFilterByField()
  const [openAddColumnDropdown, setOpenAddColumnDropdown] = useState(false)
  const [editSheetCustomModelDropdownVisibility, setEditSheetCustomModelDropdownVisibility] = useState(false)
  const { editNode } = {
    editNode: useEditNodeMutation(),
  }
  const currentLeftColumnIndex = column.index + 1
  const sheetCustomModels = useRecoilValue(sheetCustomModelsAtom)
  const isSheetCustomModelNode = sheetCustomModels.some((x) => x.name === node.name) // これ以外の方法で判定できるようにしたいが、仕方ない？
  // カスタムカラムの列の右に追加できるのはカスタムカラムだけで、「項目」「関連オブジェクト」は選べない。
  // 組織契約に customColumn が無いと選べる候補が 1 つも無くなるため、「右に一列追加」を押せなくする
  const canAddCustomColumn =
    !isSheetCustomModelNode || canUseOrganizationLicense(me.myUser, ORGANIZATION_LICENSES.customColumn)
  const { isGoalView } = context
  const changes = useRecoilValue(changesAtom)

  const menu: Array<ContextMenuItem | undefined> = [
    isGoalView
      ? undefined
      : {
          text: (
            <span>
              {t(`右に一列追加`)}
              <RightOutlined className="pl-4" />
            </span>
          ),
          icon: <PlusOutlined />,
          disabled: !canEditField || !canAddCustomColumn,
          onClick: (): void => {
            setOpenAddColumnDropdown(true)
            setEditSheetCustomModelDropdownVisibility(false)
          },
        },
    isSheetCustomModelNode
      ? {
          text: (
            <span>
              {t(`列の編集`)}
              <RightOutlined className="pl-4" />
            </span>
          ),
          icon: <EditOutlined />,
          disabled: !canEditField,
          onClick: (): void => {
            setOpenAddColumnDropdown(false)
            setEditSheetCustomModelDropdownVisibility(true)
          },
        }
      : undefined,
    isGoalView
      ? undefined
      : {
          text: t(`列のインポート`),
          icon: <ImportOutlined />,
          disabled: !canEditField,
          onClick: (): void => {
            const configField = queryFieldToConfigField(column.field, config.fields ?? [])
            if (configField !== undefined) {
              modal.confirm(buildImportFieldsModalProperties({ field: configField, setHasChangeToViewSchema }))
            }
          },
        },
    isGoalView
      ? undefined
      : {
          text: t(`列の削除`),
          icon: <DeleteOutlined />,
          disabled: !canEditField || column.columnDeletable === false,
          onClick: (): void => {
            cancel({
              field,
              onSetConfig: () => {
                setHasChangeToViewSchema(true)
              },
            })
            remove({
              me,
              config,
              field,
              onSetConfig: () => {
                setHasChangeToViewSchema(true)
              },
            })
            hide()
          },
          confirmMessage: isSheetCustomModelNode ? (
            <span>
              {t(`本当に削除しますか？`)}
              <br />
              {t(`カスタム列を削除すると、データも全て削除されます`)}
            </span>
          ) : undefined,
          tooltipMessage: canEditField && column.columnDeletable === false ? t(`この項目は削除できません`) : undefined,
        },
    {
      text: t(`フィルタ`),
      icon: <FilterOutlined />,
      disabled: !canEditField,
      onClick: (): void => {
        const m = modal.confirm(
          buildViewQueryFiltersModalProperties({
            initialFilterTree: calculateInitialFilterTree(config, field),
            onAfterFinish: () => {
              m.destroy()
            },
            view,
            setHasChangeToViewSchema,
          }),
        )
        hide()
      },
    },
    column.isFiltered
      ? {
          text: t(`フィルタの削除`),
          icon: <ClearOutlined />,
          disabled: !canEditField,
          onClick: (): void => {
            removeFilterByField({
              field,
              onSetConfig: () => {
                setHasChangeToViewSchema(true)
              },
            })
            hide()
          },
        }
      : undefined,
    node.path.length > 1
      ? {
          icon: <FilterOutlined />,
          disabled: !canEditField,
          onClick: (): void => {
            const configNode = getNode(node.name)
            if (configNode === undefined) {
              message.warning(t(`ノードが見つかりませんでした`))
            } else {
              const m = modal.confirm(
                buildViewJoinFiltersSettingModalProperties({
                  node: configNode,
                  onAfterFinish: () => {
                    m.destroy()
                  },
                  setHasChangeToViewSchema,
                }),
              )
            }
            hide()
          },
          text: t(`ブロックのフィルタ`),
        }
      : undefined,
    column.sortState === 'asc'
      ? undefined
      : {
          icon: <SortAscendingOutlined />,
          onClick: (): void => {
            upsertSorter({ field, order: 'asc', sortBy: sorterSortBy })
            setHasChangeToViewSchema(true)
            hide()
          },
          text: t(`昇順で並び替え`),
          confirmMessage: changes.length > 0 ? t('設定を変更すると変更情報が失われます。') : undefined,
        },
    column.sortState === 'desc'
      ? undefined
      : {
          icon: <SortDescendingOutlined />,
          onClick: (): void => {
            upsertSorter({ field, order: 'desc', sortBy: sorterSortBy })
            setHasChangeToViewSchema(true)
            hide()
          },
          text: t(`降順で並び替え`),
          confirmMessage: changes.length > 0 ? t('設定を変更すると変更情報が失われます。') : undefined,
        },
    column.sortState === undefined
      ? undefined
      : {
          icon: <ClearOutlined />,
          onClick: (): void => {
            cancel({
              field,
              onSetConfig: () => {
                setHasChangeToViewSchema(true)
              },
            })
            hide()
          },
          text: t(`並び替えを解除`),
          confirmMessage: changes.length > 0 ? t('設定を変更すると変更情報が失われます。') : undefined,
        },
    context.fixedLeftColumnIndex === currentLeftColumnIndex
      ? undefined
      : {
          icon: <PushpinOutlined />,
          onClick: (): void => {
            setMeta({
              config,
              newMeta: {
                // eslint-disable-next-line sonarjs/todo-tag
                fixedLeftColumnIndex: currentLeftColumnIndex, // TODO: fieldIndexを永続化すべきがcolumnIndexを保存してしまっているため、systemColumnの数が変った際に不具合になりうる
              },
            })
            setHasChangeToViewSchema(true)
            hide()
          },
          text: t(`列を固定`),
        },
    !isGoalView && !column.readonly
      ? {
          icon: <EditOutlined />,
          disabled: !canEditField,
          onClick: (): void => {
            setUiColumn({
              field,
              column: {
                creatable: false,
                updatable: false,
              },
              onSetConfig: () => {
                setHasChangeToViewSchema(true)
              },
            })
            hide()
          },
          text: t(`書き込み禁止`),
        }
      : undefined,
    !isGoalView && me.organization.isEarlyReleaseTarget
      ? {
          icon: <BarChartOutlined />,
          disabled: !canEditField,
          onClick: (): void => {
            const configField = queryFieldToConfigField(column.field, config.fields ?? [])
            if (configField !== undefined) {
              const m = modal.confirm(
                buildMeasureFormModalProperties({
                  field: configField,
                  onFinish: () => {
                    m.destroy()
                  },
                  view,
                  setHasChangeToViewSchema,
                }),
              )
            }
            hide()
          },
          text: t(`集計を追加`),
        }
      : undefined,
    isGoalView
      ? undefined
      : // eslint-disable-next-line sonarjs/no-nested-conditional
        firstRecord === undefined
        ? {
            icon: <FunctionOutlined />,
            disabled: true,
            tooltipMessage: t(`1件以上のデータが表示されている時のみ設定できます。`),
            text: t(`条件付き書式`),
          }
        : {
            icon: <FunctionOutlined />,
            disabled: !canEditField,
            onClick: () => {
              const configField = queryFieldToConfigField(column.field, config.fields ?? [])
              if (configField !== undefined) {
                const m = modal.confirm(
                  buildConditionalEffectsFormModalProperties({
                    configField,
                    onAfterFinish: () => {
                      m.destroy()
                    },
                    firstRecord,
                    column,
                    compiledConfig,
                    setHasChangeToViewSchema,
                  }),
                )
              }
              hide()
            },
            text: t(`条件付き書式`),
          },
    isGoalView
      ? undefined
      : {
          icon: <HighlightOutlined />,
          onClick: (): void => {
            const configField = queryFieldToConfigField(column.field, config.fields ?? [])
            if (configField !== undefined) {
              posthogTrackView(POSTHOG_EVENTS.view_highlight_conditional_effect_modal, {
                viewId: view.id,
                prevViewId: viewsContext.prevView?.id,
                prevViewType: viewsContext.prevView?.type,
                nodeName: undefined,
              })
              const m = modal.confirm(
                buildHighlightFormModalProperties({
                  configField,
                  columnTitle: column.title,
                  onFinish: () => {
                    m.destroy()
                  },
                  view,
                  setHasChangeToViewSchema,
                }),
              )
            }
            hide()
          },
          text: (
            <span>
              {t(`ハイライト条件`)}
              <Tag className="ml-1 opacity-80" color="blue">
                {t(`β版`)}
              </Tag>
            </span>
          ),
        },
    context.fixedLeftColumnIndex === currentLeftColumnIndex
      ? {
          icon: <PushpinOutlined />,
          onClick: (): void => {
            setMeta({
              config,
              newMeta: {
                fixedLeftColumnIndex: undefined,
              },
            })
            setHasChangeToViewSchema(true)
            hide()
          },
          text: t(`列の固定を解除`),
        }
      : undefined,
    !isGoalView && column.readonly
      ? {
          icon: <EditOutlined />,
          disabled: !canEditField,
          onClick: (): void => {
            setUiColumn({
              field,
              column: {
                creatable: true,
                updatable: true,
              },
              onSetConfig: () => {
                setHasChangeToViewSchema(true)
              },
            })
            hide()
          },
          text: t(`書き込み禁止を解除`),
        }
      : undefined,
    column.visible === true
      ? {
          icon: <EyeInvisibleOutlined />,
          disabled: !canEditField,
          onClick: (): void => {
            setUiColumn({
              field,
              column: {
                visible: false,
              },
              onSetConfig: () => {
                setHasChangeToViewSchema(true)
              },
            })
            hide()
          },
          text: t(`列を非表示`),
        }
      : undefined,
    !isGoalView &&
    (column.field.meta.fieldMetaType === fieldMetaTypes.enum.select ||
      column.field.meta.fieldMetaType === fieldMetaTypes.enum.multi_select)
      ? {
          text: t(`連動項目を作成`),
          icon: <NodeIndexOutlined />,
          disabled: !canEditField,
          onClick: (): void => {
            const m = modal.confirm(
              buildDynamicSelectOptionModalProperties({
                column,
                onAfterFinish: () => {
                  m.destroy()
                },
                setHasChangeToViewSchema,
              }),
            )
            hide()
          },
        }
      : undefined,
    isGoalView
      ? undefined
      : {
          icon: <SettingOutlined />,
          disabled: !canEditField,
          onClick: (): void => {
            const m = modal.confirm(
              buildViewQueryNodeDetailModalProperties({
                nodeName: node.name,
                onAfterFinish: () => {
                  m.destroy()
                },
                compiledConfig,
                query,
                setHasChangeToViewSchema,
              }),
            )
            hide()
          },
          text: t(`高度な設定`),
        },
    // admin
    ...(canViewSearchSqlModal(me)
      ? [
          field.read.searchSql === undefined
            ? undefined
            : {
                icon: <SearchOutlined />,
                disabled: !canEditField,
                onClick: (): void => {
                  const m = modal.confirm(
                    buildSearchSqlFormModalProperties({
                      content: field,
                      onAfterFinish: () => {
                        m.destroy()
                      },
                      setHasChangeToViewSchema,
                    }),
                  )
                  hide()
                },
                text: t(`検索設定`),
                className: CONSTANT.colorClasses.ADMIN_TEXT_COLOR,
              },
          node.read.join?.search.sql !== undefined && node.read.join.relation === 'many_to_one'
            ? {
                icon: <SearchOutlined />,
                disabled: !canEditField,
                onClick: (): void => {
                  const configNode = getNode(node.name)
                  if (configNode !== undefined) {
                    const m = modal.confirm(
                      buildNodeSearchSqlFormModalProperties({
                        node: configNode,
                        editNode: (sql: string) => {
                          editNode({
                            node: configNode,
                            f: (oldNode: ViewConfigTreeNode) => {
                              // eslint-disable-next-line no-param-reassign
                              oldNode.override = {
                                ...oldNode.override,
                                searchSql: sql,
                              }
                            },
                            onSetConfig: () => {
                              setHasChangeToViewSchema(true)
                            },
                          })
                        },
                        onAfterFinish: () => {
                          m.destroy()
                        },
                      }),
                    )
                  }
                  hide()
                },
                text: t(`ブロックの検索設定`),
                className: CONSTANT.colorClasses.ADMIN_TEXT_COLOR,
              }
            : undefined,
          {
            icon: <ConsoleSqlOutlined />,
            onClick: (): void => {
              const { node, ...others } = column
              const m = modal.confirm(
                buildViewDebugModalProperties({
                  content: others,
                  onAfterFinish: () => {
                    m.destroy()
                  },
                }),
              )
              hide()
            },
            text: t(`スキーマ表示`),
            className: CONSTANT.colorClasses.ADMIN_TEXT_COLOR,
          },
        ]
      : []),
    // 2023/01/30 widthを0にする目的で、設定編集をしたいという声があったため
    ...(canEditViewConfigField(me)
      ? [
          {
            icon: <ConsoleSqlOutlined />,
            onClick: (): void => {
              const configField = queryFieldToConfigField(column.field, config.fields ?? [])
              if (configField !== undefined) {
                const m = modal.confirm(
                  buildViewDebugModalProperties({
                    content: configField,
                    // eslint-disable-next-line sonarjs/todo-tag
                    schema: viewConfigFieldSchema, // TODO: 指定はしているものの、現状ワークしていない
                    onChange(newField) {
                      setField({
                        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
                        field: newField as ViewConfigField,
                        onSetConfig: () => {
                          setHasChangeToViewSchema(true)
                        },
                      })
                    },
                    onAfterFinish: () => {
                      m.destroy()
                    },
                  }),
                )
              }
              hide()
            },
            text: t(`設定編集`),
            className: CONSTANT.colorClasses.ADMIN_TEXT_COLOR,
          },
        ]
      : []),
  ]

  return (
    <div
      ref={reference}
      style={{
        ...menuStyle,
      }}
    >
      {menu.filter(isNotNullish).map((item, index) => (
        <PopconfirmIf
          key={index}
          condition={item.confirmMessage !== undefined}
          title={<span>{item.confirmMessage}</span>}
          onConfirm={() => {
            item.onClick?.()
          }}
        >
          <Tooltip title={getTooltipMessage(item)}>
            <Button
              type="text"
              style={{ width: '100%', textAlign: 'left' }}
              icon={item.icon}
              disabled={item.disabled}
              onClick={item.confirmMessage === undefined ? item.onClick : undefined}
              className={`${item.className ?? ''} justify-start`}
            >
              {item.text}
            </Button>
          </Tooltip>
        </PopconfirmIf>
      ))}
      {openAddColumnDropdown && (
        <div
          className="absolute top-0 bg-white p-2"
          style={{
            ...menuStyle,
            width: 400, // ここを変更する際は、transition.lessでslide-leftも変更する必要がある
            top: 0,
            left: width + 1,
          }}
          onKeyDown={(event) => {
            // シートのイベントが発火しない様にイベントの伝播を止める
            event.stopPropagation()
          }}
        >
          <div>
            <PropertySelectorTableForContextMenu
              initialNodePath={node.path}
              configIndex={column.configIndex}
              columns={columns}
              compiledConfig={compiledConfig}
              view={view}
              query={query}
              setHasChangeToViewSchema={setHasChangeToViewSchema}
            />
          </div>
        </div>
      )}
      {editSheetCustomModelDropdownVisibility && (
        <div
          className="absolute top-0 bg-white p-2"
          style={{
            ...menuStyle,
            width: 400, // ここを変更する際は、transition.lessでslide-leftも変更する必要がある
            top: 0,
            left: width + 1,
          }}
        >
          <div>
            <SheetCustomModelPropertyEditForm
              node={node}
              propertyName={column.configField?.property.propertyName ?? ''}
            />
          </div>
        </div>
      )}
    </div>
  )
}

function getTooltipMessage(item: ContextMenuItem): string | undefined {
  if (item.tooltipMessage !== undefined) {
    return item.tooltipMessage
  }

  if (item.disabled === true) {
    return t(`権限がありません`)
  }
}

function calculateInitialFilterTree(config: ViewConfigSheet, field: ViewQueryField): ViewConfigFilterNodeForForm {
  const tree = config.filterTree ?? {
    logicalOperator: 'and',
    leafs: [],
    children: [],
  }
  const treeForForm = convertToFilterNodeForForm(tree)

  const configField = queryFieldToConfigField(field, config.fields ?? [])
  const shouldAddLeaf =
    configField !== undefined &&
    treeForForm.leafs.every(
      (leaf) => leaf.type === 'property' && !isSameNodeProperty(leaf.property, configField.property),
    )
  if (!shouldAddLeaf) return treeForForm

  return {
    ...treeForForm,
    leafs: [
      ...treeForForm.leafs,
      {
        type: 'property' as const,
        property: configField.property,
        option: { shouldConsiderCase: true },
      },
    ],
  }
}
